/*
 * @Description: 多币种价格相关的常量和方法
 * @FilePath: \assets\js\currency-price.js
 */

import type { CartListsItem } from '@/api/types/cart';
import type {
  CurrencyItemList,
  ICurrencySymbol,
  IPriceAmount,
  IPriceInfo,
  IPriceInfoList,
} from '@/api/types/currency-price';
import { useStore } from '@/store';
import { accMul, add } from './utils';

/**
 * @description: 根据多币种价格列表获取当前币种的价格信息
 * @param priceList 价格列表
 * @param currencyName 指定币种
 * @return 当前币种的价格对象，未找到时返回空价格
 */
export function genPrice(priceList: IPriceInfoList, currencyName?: string): IPriceAmount {
  const store = useStore();
  try {
    const name = currencyName ?? store.currency.name;
    const price = priceList.find((item) => item.currency === name);
    if (price) {
      if (price.sell_price && price.price != price.sell_price) {
        price.discount = true;
      }
      // Ps: 这里一定要返回原始的price，因为配镜流程场景里 price 的结构是
      // {difference_price: '', difference_sell_price: '', currency: ''}
      return price;
    }
  } catch (error) {
    console.log(error);
  }
  return { price: '', sell_price: '' };
}

/**
 * @description: 获取单品价格（只用于购物车列表中的数据）
 * @param item 商品信息（购物车列表中的一项）
 * @param isOrigin 是否是原价，默认 false
 * @param currencyName 指定币种
 * @return 价格字符串，保留 2 位小数
 */
export function genItemPrice(item: CartListsItem, isOrigin = false, currencyName?: string): string {
  try {
    let framePrice: string | number = 0;
    let lensPrice: string | number = 0;
    let coatingPrice: string | number = 0;
    let clipPrice = 0;
    if (item.product?.price_info) {
      const fp = genPrice(item.product.price_info, currencyName);
      framePrice = (isOrigin ? fp.price : fp.sell_price) ?? 0;
    }
    if (item.lens?.price_info) {
      const lp = genPrice(item.lens.price_info, currencyName);
      lensPrice = (isOrigin ? lp.price : lp.sell_price) ?? 0;
    }
    if (item.coating?.price_info) {
      const cp = genPrice(item.coating.price_info, currencyName);
      coatingPrice = (isOrigin ? cp.price : cp.sell_price) ?? 0;
    }
    if (item.clips?.length) {
      item.clips.forEach((clip) => {
        const clipP = genPrice(clip.price_info ?? [], currencyName);
        const unit = isOrigin ? clipP.price : clipP.sell_price;
        clipPrice += Number(unit) * (clip?.qty ?? 0);
      });
    }
    return add(
      accMul(add(add(framePrice, lensPrice), coatingPrice), item.qty ?? 0),
      clipPrice,
    ).toFixed(2);
  } catch (error) {
    console.log(error);
  }
  return '';
}

/**
 * @description: 获取单品处方价格（包括所选的镜片以及镀膜）（目前用于购物车列表中的数据）
 * @param {*} item 商品信息（目前是购物车列表中的一项）
 * @return {*} 价格(保留2位小数的String)
 */
export function getpresPrice(item: CartListsItem): string {
  try {
    let lensPrice: string | number = 0;
    let coatingPrice: string | number = 0;
    if (item.lens?.price_info) {
      const lp = genPrice(item.lens.price_info);
      lensPrice = lp.sell_price ?? 0;
    }
    if (item.coating?.price_info) {
      const cp = genPrice(item.coating.price_info);
      coatingPrice = cp.sell_price ?? 0;
    }
    return add(lensPrice, coatingPrice).toFixed(2);
  } catch (error) {
    console.log(error);
  }

  return '';
}

/**
 * @description: 获取订单里的币种符号（目前用于订单列表中的数据）
 * @param val 币种名称
 * @return 币种符号对象或空字符串
 */
export function getpayCurrency(val: string): ICurrencySymbol {
  try {
    const store = useStore();
    const currency = (store.currencyList as CurrencyItemList).find((item) => item.name === val);
    if (currency) {
      return {
        symbol_prefix: currency.symbol_prefix ?? '',
        symbol_suffix: currency.symbol_suffix ?? '',
      };
    }
  } catch (error) {
    console.log(error);
  }
  return {
    symbol_prefix: '',
    symbol_suffix: '',
  };
}

/**
 * @description: 根据当前币种获取运营自定义的币种符号
 * @param currencyName 币种名称，不传则用 store 当前币种
 * @return 对应的币种符号
 */
export function currencyFilter(currencyName?: string): string {
  const store = useStore();
  const _currencyName = currencyName ?? store.currency.name;
  try {
    const currencyArr: Array<{ name: string; symbol: string }> = [
      { name: 'USD', symbol: '$' },
      { name: 'AUD', symbol: 'A$' },
      { name: 'GBP', symbol: '£' },
      { name: 'CAD', symbol: 'C$' },
      { name: 'EUR', symbol: '€' },
    ];
    const newArr = currencyArr.find((item) => item.name === _currencyName);
    return newArr?.symbol ?? '';
  } catch (error) {
    console.log(error);
  }
  return store.currency.symbol || '';
}

/**
 * @description: 获取单品基础币种（美元）价格（只用于购物车列表中的数据）
 * @param item 商品信息（购物车列表中的一项）
 * @param isOrigin 是否是原价，默认 false
 * @return 价格字符串，保留 2 位小数
 */
export function genItemBasePrice(item: CartListsItem, isOrigin = false): string {
  return genItemPrice(item, isOrigin, 'USD');
}

/**
 * @description: 获取商品折扣比例
 * @param price_info 商品多币种价格列表
 * @return 折扣比例数值（0–100 的整数）
 */
export function priceScale(price_info: IPriceInfo[]): number {
  const store = useStore();
  const sell = genPrice(price_info, store.currency.name).sell_price;
  const origin = genPrice(price_info, store.currency.name).price;
  const sellNum = Number(sell);
  const originNum = Number(origin);
  const difference = originNum > 0 ? sellNum / originNum : 0;
  return Math.floor((1 - difference) * 100);
}
